import { useCallback, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import * as Sentry from '@sentry/react';
import { CALLABLE_BEER_ADD_BEER, functions } from '../utils/firebase';
import { processImportResponse } from '../utils/addBeerResponse';
import { buildImportBatches } from '../utils/sheetBeerImport/transformRowsToAddBeerPayload';
import { mergeImportResponses } from '../utils/sheetBeerImport/mergeImportResponses';

/**
 * useSheetBeerImport
 * Imports validated sheet rows via sequential addBeer batches grouped by brewery.
 */
export function useSheetBeerImport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });

  const importSheetBeers = useCallback(
    async ({
      validatedRows,
      breweryIdByGroupKey,
      selectedRowIndices,
      sourceFilename,
    }) => {
      setLoading(true);
      setError(null);

      const batches = buildImportBatches(
        validatedRows,
        breweryIdByGroupKey,
        selectedRowIndices,
      );

      if (!batches.length) {
        setError('No beers selected to import.');
        setProgress({ completed: 0, total: 0 });
        setLoading(false);
        return null;
      }

      setProgress({ completed: 0, total: batches.length });

      try {
        const addBeerFn = httpsCallable(functions, CALLABLE_BEER_ADD_BEER, {
          timeout: 600000,
        });

        const responses = [];
        for (let batchIndex = 0; batchIndex < batches.length; batchIndex += 1) {
          const batch = batches[batchIndex];
          const result = await addBeerFn({
            beers: batch.beers,
            breweryId: batch.breweryId,
            sourceType: 'sheet_import',
            source: sourceFilename || 'sheet_import',
          });
          responses.push(processImportResponse(result?.data ?? {}));
          setProgress({ completed: batchIndex + 1, total: batches.length });
        }

        return mergeImportResponses(responses);
      } catch (importError) {
        console.error('Error importing sheet beers:', importError);
        Sentry.captureException(importError);
        setError(importError?.message || 'Failed to import beers. Please try again.');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const resetProgress = useCallback(() => {
    setProgress({ completed: 0, total: 0 });
    setError(null);
  }, []);

  return {
    importSheetBeers,
    loading,
    error,
    progress,
    resetProgress,
  };
}
